import "canbook-style/globals.css";
import "@/components/home/swiper.css";
import "canbook-style/ui/input/swiper.css";
import "canbook-style/ui/image/swiper.css";
import "@/pages/bookings/_components/style.css";
import type { AppProps } from "next/app";
import UserProvider from "canbook-data/user/user-provider";
import AnalyticsProvider from "canbook-data/analytics/provider";
import BusinessProvider from "canbook-data/business/provider";
import OnboardBusinessProvider from "./onboard/provider";
import ConversationProvider from "canbook-data/messages/message-provider";
import CampaignProvider from "canbook-data/campaign/provider";
import CampaignFilterProvider from "canbook-data/campaign/filter-provider";
import WhatsAppCustomHeaderProvider from "canbook-data/whatsapp/custom-header/provider";
import WhatsappTemplateProvider from "canbook-data/whatsapp/message-templates/provider";
import WhatsAppConversationProvider from "canbook-data/whatsapp/messages/message-provider";
import type { NextPage } from "next";
import type { ReactElement } from "react";
import { NextUIProvider } from "@nextui-org/react";
import Head from "next/head";
import { useEffect } from "react";
import WalkInProvider from "canbook-data/visit/provider";

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactElement;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

function SentryInit() {
  useEffect(() => {
    // Only initialize Sentry in production
    if (process.env.NODE_ENV !== "production") {
      return;
    }

    const script = document.createElement("script");
    script.src =
      "https://js-de.sentry-cdn.com/9d88914b061274421b1b49136e8e23b6.min.js";
    script.crossOrigin = "anonymous";

    script.onload = () => {
      if ((window as any).Sentry) {
        (window as any).Sentry.init({
          dsn: "https://9d88914b061274421b1b49136e8e23b6@o4509637709332480.ingest.de.sentry.io/4509677622919248",
          tracesSampleRate: 1.0,
        });
      }
    };

    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  return null;
}

export default function App({ Component, pageProps }: AppPropsWithLayout) {
  const getLayout = Component.getLayout || ((page) => page);

  return (
    <UserProvider>
      <AnalyticsProvider>
        <BusinessProvider>
          <OnboardBusinessProvider>
            <ConversationProvider>
              <CampaignFilterProvider>
                <CampaignProvider>
                  <WhatsappTemplateProvider>
                    <WhatsAppCustomHeaderProvider>
                      <WhatsAppConversationProvider>
                        <WalkInProvider>
                          <NextUIProvider>
                            <Head>
                              <title>
                                Canbook | Get your appointments & Service
                                bookings online
                              </title>
                            </Head>
                            <SentryInit />
                            {getLayout(<Component {...pageProps} />)}
                          </NextUIProvider>
                        </WalkInProvider>
                      </WhatsAppConversationProvider>
                    </WhatsAppCustomHeaderProvider>
                  </WhatsappTemplateProvider>
                </CampaignProvider>
              </CampaignFilterProvider>
            </ConversationProvider>
          </OnboardBusinessProvider>
        </BusinessProvider>
      </AnalyticsProvider>
    </UserProvider>
  );
}
